import React, { useEffect, useState } from 'react';
import axios from 'axios';
import LightAdmin from 'components/headers/lightAdmin';
import AnimationRevealPage from 'helpers/AnimationRevealPage';
import Footer from "components/footers/Footer";
import './style.css';
import Swal from 'sweetalert2';
import { useTranslation } from 'react-i18next';

const PaymentAdmin = () => {
    const { t, i18n } = useTranslation();
    const [orders, setOrders] = useState([]);
    const [orderStats, setOrderStats] = useState({
        total: 0,
        pending: 0,
        completed: 0,
        cancelled: 0,
        refunded: 0,
        totalRevenue: 0,
    });
    const [pagination, setPagination] = useState({
        currentPage: 1,
        totalPages: 1,
        perPage: 10,
    });
    const [isAdmin, setIsAdmin] = useState(false);

    useEffect(() => {
        checkAdminStatus();
    }, []);

    // 🟢 FIXED: Synchronized pagination hook
    useEffect(() => {
        if (isAdmin) {
            fetchAllOrders(pagination.currentPage);
        }
    }, [pagination.currentPage, isAdmin]);

    const checkAdminStatus = () => {
        const adminCheck = localStorage.getItem('isAdmin') === '1';
        setIsAdmin(adminCheck);

        if (adminCheck) {
            fetchAllOrders(pagination.currentPage);
        }
    };

    const fetchAllOrders = async (page) => {
        try {
            const token = localStorage.getItem('authToken');
            if (!token) return;

            const response = await axios.get(`https://jolyaparis.cloud/api/admin/orders?page=${page}&per_page=${pagination.perPage}`, {
                headers: {
                    'Content-Type': 'application/json',
                    Authorization: `Bearer ${token}`,
                },
            });

            if (response.status === 200) {
                const or = response.data.data || [];
                setOrders(or);
                setPagination(prev => ({
                    ...prev,
                    totalPages: response.data.last_page || 1,
                }));
                calculateOrderStats(or);
            }
        } catch (error) {
            console.error("Error fetching admin orders:", error);
        }
    };

    const calculateOrderStats = (ordersList) => {
        const total = ordersList.length;
        const pending = ordersList.filter(order => order.status === 'en attente').length;
        const completed = ordersList.filter(order => order.status === 'completed').length;
        const cancelled = ordersList.filter(order => order.status === 'cancelled').length;
        const refunded = ordersList.filter(order => order.status === 'refunded').length;
        const totalRevenue = ordersList.reduce((sum, order) => sum + parseFloat(order.payment?.Amount || 0), 0);

        setOrderStats({
            total,
            pending,
            completed,
            cancelled,
            refunded,
            totalRevenue,
        });
    };

    const handlePaymentStatus = async (orderId, status) => {
        try {
            const token = localStorage.getItem('authToken');
            if (!token) return;

            const response = await axios.put(`https://jolyaparis.cloud/api/admin/orders/${orderId}/payment-status`,
                { PaymentStatus: status },
                {
                    headers: {
                        'Content-Type': 'application/json',
                        Authorization: `Bearer ${token}`,
                    }
                }
            );

            if (response.status === 200) {
                fetchAllOrders(pagination.currentPage);
            }
        } catch (error) {
            console.error("Error updating payment status:", error);
        }
    };

    const handleView = (orderID) => {
        const order = orders.find(o => o.OrderID === orderID);
        if (order && order.Status === "livré") {
            handlePaymentStatus(order.OrderID, 'payé');
        } else {
            Swal.fire({
                icon: 'warning',
                title: 'Attention!',
                text: "Vous ne pouvez pas marquer cette commande comme payée tant qu'elle n'est pas livrée.",
            });
        }
    };

    // 🟢 FIXED: Single unified pagination handler linked to pagination state
    const handlePageChange = (newPage) => {
        if (newPage >= 1 && newPage <= pagination.totalPages) {
            setPagination(prev => ({
                ...prev,
                currentPage: newPage,
            }));
        }
    };

    if (!isAdmin) {
        return <p className="text-danger text-center mt-5">Unauthorized: You do not have admin access.</p>;
    }

    return (
        <AnimationRevealPage>
            <LightAdmin />
            <div className="d-flex">
                <section className="w-100 p-3">
                    <div className="d-flex justify-content-start align-items-center gap-2">
                        <h1 className="text-dark display-4">{t('Tableau_de_bord_paiements')}</h1>
                    </div>
                    <hr className="my-4" />

                    {/* Orders Table */}
                    <div className="table-responsive">
                        <table className="table table-striped table-hover align-middle">
                            <thead className="table-primary">
                                <tr>
                                    <th>{t('ID_de_commande')}</th>
                                    <th>{t('Fournisseur')}</th>
                                    <th>{t('Paiement')}</th>
                                    <th>{t('Produits')}</th>
                                    <th>{t('Montant')}</th>
                                    <th>{t('statut')}</th>
                                    <th>{t('Cree_le')}</th>
                                    <th>{t('Mis_a_jour_le')}</th>
                                    <th>{t('Actions')}</th>
                                </tr>
                            </thead>
                            <tbody>
                                {orders.length > 0 ? (
                                    orders.map((order) => (
                                        <tr key={order.OrderID}>
                                            <td>{order.idsOrder}</td>
                                            <td>
                                                <strong>{order.supplier?.name || 'Commandes passe sans code promo'}</strong><br />
                                                Phone: {order.supplier?.phone || 'N/A'}<br />
                                                Custom ID: {order.supplier?.custom_id || 'N/A'}
                                            </td>
                                            <td>
                                                Statut du paiement: {order.payment?.PaymentStatus || 'N/A'}<br />
                                                Montant: {order.payment?.Amount || 0}<br />
                                                Date: {order.payment?.PaymentDate ? new Date(order.payment.PaymentDate).toLocaleString() : 'N/A'}
                                            </td>
                                            <td>
                                                {order.order_items && order.order_items.length > 0 ? (
                                                    order.order_items.map((item) => {
                                                        // Safely resolve product name handling translations or string fallbacks
                                                        const productName = typeof item.product?.Name === 'object'
                                                            ? (item.product.Name[i18n.language] || item.product.Name['fr'] || 'Produit')
                                                            : (item.product?.Name || 'Produit');

                                                        return (
                                                            <div key={item.OrderItemID} className="mb-1">
                                                                <strong>{productName}</strong> (x{item.Quantity})
                                                            </div>
                                                        );
                                                    })
                                                ) : (
                                                    <span className="text-muted">Aucun produit</span>
                                                )}
                                            </td>
                                            <td>
                                                <strong>{order.payment?.Amount || order.total} {t("currency")}</strong>
                                            </td>
                                            <td>{order.Status}</td>
                                            <td>{new Date(order.created_at).toLocaleString()}</td>
                                            <td>{new Date(order.updated_at).toLocaleString()}</td>

                                            {/* Action Buttons */}
                                            <td>
                                                <button
                                                    className="btn btn-info btn-sm"
                                                    onClick={() => handleView(order.OrderID)}
                                                    disabled={order.payment?.PaymentStatus === 'payé' || order.Status !== 'livré'}
                                                    title={order.Status !== 'livré' ? "Cette commande n'est pas encore livrée." : ""}
                                                >
                                                    {t('Paye')}
                                                </button>
                                            </td>
                                        </tr>
                                    ))
                                ) : (
                                    <tr>
                                        <td colSpan="9" className="text-center py-4">Aucune commande trouvée.</td>
                                    </tr>
                                )}
                            </tbody>
                        </table>
                    </div>

                    {/* Pagination Controls */}
                    {pagination.totalPages > 1 && (
                        <div className="d-flex justify-content-center mt-4">
                            <nav>
                                <ul className="pagination">
                                    <li className={`page-item ${pagination.currentPage === 1 ? 'disabled' : ''}`}>
                                        <button className="page-link" onClick={() => handlePageChange(pagination.currentPage - 1)}>
                                            {t('Previous')}
                                        </button>
                                    </li>
                                    {[...Array(pagination.totalPages)].map((_, index) => (
                                        <li key={index} className={`page-item ${pagination.currentPage === index + 1 ? 'active' : ''}`}>
                                            <button className="page-link" onClick={() => handlePageChange(index + 1)}>
                                                {index + 1}
                                            </button>
                                        </li>
                                    ))}
                                    <li className={`page-item ${pagination.currentPage === pagination.totalPages ? 'disabled' : ''}`}>
                                        <button className="page-link" onClick={() => handlePageChange(pagination.currentPage + 1)}>
                                            {t('Next')}
                                        </button>
                                    </li>
                                </ul>
                            </nav>
                        </div>
                    )}
                </section>
            </div>
            <Footer />
        </AnimationRevealPage>
    );
};

export default PaymentAdmin; // (Ensure regular export default)